
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Search } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import VimeoPlayer from '@/components/vimeo/VimeoPlayer';
import MiniPlayerWrapper from '@/components/video/MiniPlayerWrapper';
import { validateImageUrl, optimizeContentfulImage } from '@/utils/imageUtils';
import { useIsMobile } from '@/hooks/use-mobile';

interface ContentRendererProps {
  content: string;
  contentImageUrls: string[];
  handleImageClick: (index: number) => void;
}

const ContentRenderer: React.FC<ContentRendererProps> = ({
  content,
  contentImageUrls,
  handleImageClick
}) => {
  const { resolvedTheme } = useTheme();
  const isMobile = useIsMobile();
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const processedContentRef = useRef<string>('');
  const [hoveredImageIndex, setHoveredImageIndex] = useState<number | null>(null);

  useEffect(() => {
    console.log('ContentRenderer - Content preview:', content.substring(0, 200) + '...');
    console.log('ContentRenderer - Content images available:', contentImageUrls);
  }, [content, contentImageUrls]);

  const processContent = (content: string) => {
    if (!content) return '';
    let processedContent = content.replace(/\[vimeo:([\w-]+)\]/g, (match, vimeoId) => {
      return `\n\n<vimeo-player vimeo-id="${vimeoId}"></vimeo-player>\n\n`;
    });
    processedContent = processedContent.replace(/\[contentful-image:([\w-]+)\]/g, (match, fieldId) => {
      return match;
    });
    processedContentRef.current = processedContent;
    return processedContent;
  };

  const processedContent = processContent(content);

  // Use a simple render-order counter as a robust fallback for image indexing.
  // This aligns with the order of images in the markdown, which matches contentImageUrls.
  let renderImageCounter = 0;

  return (
    <article className={`prose prose-lg max-w-none leading-article ${resolvedTheme === 'dark' ? 'prose-invert' : ''}`}>
      <ReactMarkdown
        components={{
          p: ({ node, ...props }) => {
            const content = props.children?.toString() || '';
            if (content.trim().startsWith('<vimeo-player') && content.trim().endsWith('</vimeo-player>')) {
              const vimeoIdMatch = content.match(/vimeo-id="([^"]+)"/);
              const vimeoId = vimeoIdMatch ? vimeoIdMatch[1] : '';
              if (vimeoId) {
                return (
                  <div className="my-4 w-full max-w-full">
                    <MiniPlayerWrapper title="Article video" disableOnMobile={true}>
                      <VimeoPlayer
                        vimeoId={vimeoId}
                        minHeight={isMobile ? "auto" : "400px"}
                        title="Article video"
                        responsive={true}
                      />
                    </MiniPlayerWrapper>
                  </div>
                );
              }
            }
            if (content.trim().match(/^\[contentful-image:[\w-]+\]$/)) {
              return null;
            }
            return <p className="font-normal text-base text-left break-words mb-8 leading-[1.9] py-0">{props.children}</p>;
          },
          h2: ({ node, ...props }) => (
            <h2 className={cn("font-chroma font-normal text-2xl md:text-3xl mt-10 mb-4", textColor)} {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className={cn("font-chroma font-normal text-xl md:text-2xl mt-8 mb-3", textColor)} {...props} />
          ),
          img: ({ node, ...props }) => {
            const imgSrc = props.src || '';
            const imgAlt = props.alt || "Article image";

            // Try to find index by URL match (normalized)
            const imgIndex = contentImageUrls.findIndex((url) => {
              const normalizedSrc = imgSrc.replace(/^https?:\/\//, '').split('?')[0];
              const normalizedUrl = (url || '').replace(/^https?:\/\//, '').split('?')[0];
              return normalizedSrc === normalizedUrl;
            });

            // Fallback index based on render order
            const fallbackIndex = renderImageCounter++;
            const safeFallbackIndex = Math.max(0, Math.min(fallbackIndex, contentImageUrls.length - 1));

            const finalIndex = imgIndex !== -1 ? imgIndex : safeFallbackIndex;

            let imgUrl = validateImageUrl(imgSrc);
            if (imgUrl.includes('images.ctfassets.net')) {
              imgUrl = optimizeContentfulImage(imgSrc);
            }

            return (
              <div
                className="my-8 group relative cursor-pointer w-full max-w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  handleImageClick(finalIndex);
                  console.log('Image clicked, opening modal with index:', finalIndex, {
                    matchedByUrl: imgIndex !== -1,
                    fallbackIndex: safeFallbackIndex,
                    imgSrc,
                  });
                }}
              >
                <AspectRatio ratio={16 / 9} className="overflow-hidden bg-gray-100">
                  <img
                    src={imgUrl}
                    alt={imgAlt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onMouseEnter={() => setHoveredImageIndex(finalIndex)}
                    onMouseLeave={() => setHoveredImageIndex(null)}
                    onLoad={() => console.log(`Image loaded successfully: ${imgUrl}`)}
                    onError={(e) => {
                      console.error(`Failed to load image: ${imgUrl}`);
                      if (imgUrl.includes('?')) {
                        const baseUrl = imgUrl.split('?')[0];
                        console.log(`Trying fallback URL: ${baseUrl}`);
                        e.currentTarget.src = baseUrl;
                      } else {
                        e.currentTarget.src = '/placeholder.svg';
                      }
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 py-0">
                    <Search size={30} className="text-white" />
                  </div>
                </AspectRatio>
              </div>
            );
          },
          blockquote: ({ node, ...props }) => (
            <blockquote
              className={cn(
                "border-l-4 border-primary pl-4 italic my-6 text-xl font-chroma font-normal",
                resolvedTheme === 'dark' ? 'text-gray-300' : 'text-black'
              )}
              {...props}
            />
          )
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </article>
  );
};

export default ContentRenderer;
